import { useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { useLocalStorage } from "@shared/hookHelpers";
import TextButton from "@shared/v2/TextButton";
import Tooltip from "@shared/v2/Tooltip";
import { mapAlertToState, mapStateToAlert } from "@shared/ListingAlertHelpers";
import { getAlerts } from "./service";
import { truncateText } from "./helper";
import ViewAllListingAlertsModal from "../modals/ViewAllListingAlertsModal";
import AddListingAlertModal from "../modals/AddListingAlertModal";
import EditListingAlertModal from "../modals/EditListingAlertModal";
import useAlertInitialization from "./useAlertInitialization";

const AllListingAlertPreview = ({
  person,
  emailDetails = null,
  geoCodeAccountAddress = "",
  doesLeadHaveApp = false,
  onToggleCollectionNotification = null,
}) => {
  const [editListingAlertModalOpen, setEditListingAlertModalOpen] = useState(false);
  const [addListingAlertModalOpen, setAddListingAlertModalOpen] = useState(false);
  const [listingAlertsModalOpen, setListingAlertsModalOpen] = useState(false);
  const [indexOfAlertToEdit, setIndexOfAlertToEdit] = useState(0);
  const [listingAlerts, setListingAlerts] = useState([]);
  const [mlsIds, setMlsIds] = useState(null);

  const [websiteUrl, setWebsiteUrl] = useState("");
  const [isDismiss, setIsDismiss] = useLocalStorage("dismissAlert", false);

  const { errorMessage } = useAlertInitialization({
    personId: person?.id,
    mlsIds,
  });

  const onDismiss = () => {
    setIsDismiss(true);
  };

  const openListingAlertModal = async () => {
    if (emailDetails) {
      const response = await getAlerts(person.slug, { includeMlsIds: true });
      setListingAlerts(response.data.alerts);
      setWebsiteUrl(response.data.website_url);
      setMlsIds(response.data.mls_ids);
      setListingAlertsModalOpen(true);
    }
  };

  const closeListingAlertModal = () => {
    setListingAlertsModalOpen(false);
  };

  const closeEditListingAlertModal = () => {
    setEditListingAlertModalOpen(false);
    setIndexOfAlertToEdit(0);
  };

  const toggleNotificationCollection = (alertIndex, alertId, alertData, value) => {
    if (errorMessage) {
      onToggleCollectionNotification({ show: true, type: "error", msg: errorMessage });
      setTimeout(() => {
        onToggleCollectionNotification({ show: false, type: "", msg: "" });
      }, 5000);
      return;
    }
    const alert = {
      ...alertData,
      ...(doesLeadHaveApp && { activeForAppNotifications: value }),
      activeForEmails: value,
    };
    const payload = mapAlertToState(alert);

    const url = `/people/${person.id}/listing_alerts/${alertId}`;
    axios
      .patch(url, mapStateToAlert(payload), {
        headers: {
          "X-CSRF-Token": ReactOnRails.authenticityToken(),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then(() => {
        const updatedListingAlerts = [...listingAlerts];
        updatedListingAlerts[alertIndex] = {
          ...updatedListingAlerts[alertIndex],
          ...(doesLeadHaveApp && {
            activeForAppNotifications: alert.activeForAppNotifications,
          }),
          activeForEmails: alert.activeForEmails,
        };
        setListingAlerts(updatedListingAlerts);

        const msg = value
          ? `Lead will be notified of updates to properties in ${truncateText(alertData.searchName, 30)}`
          : `Lead will stop receiving notifications of updates to properties in ${truncateText(alertData.searchName, 30)}`;
        onToggleCollectionNotification({ show: true, type: "success", msg });
        setTimeout(() => {
          onToggleCollectionNotification({ show: false, type: "", msg: "" });
        }, 5000);
      })
      .catch((response) => {
        const msg = `An issue occurred when updating the notification setting for ${truncateText(alertData.searchName, 30)}. Try again later.`;
        onToggleCollectionNotification({ show: true, type: "error", msg });
        setTimeout(() => {
          onToggleCollectionNotification({ show: false, type: "", msg: "" });
        }, 5000);
        console.log(response);
      });
  };

  const onToggleNotification = (alertIndex, value) => {
    toggleNotificationCollection(
      alertIndex,
      listingAlerts[alertIndex].alertId,
      listingAlerts[alertIndex],
      value,
    );
  };

  const deleteAlert = (alertIndex) => {
    const { alertId } = listingAlerts[alertIndex];
    const url = `/people/${person.id}/listing_alerts/${alertId}`;
    axios
      .delete(url, { params: { authenticity_token: ReactOnRails.authenticityToken() } })
      .then(() => {
        setListingAlerts(listingAlerts.filter((alert) => alert.alertId !== alertId));
      })
      .catch((response) => console.log(response));
  };

  const openEditFromViewAll = (alertIndex) => {
    setIndexOfAlertToEdit(alertIndex);
    setEditListingAlertModalOpen(true);
    setListingAlertsModalOpen(false);
  };

  const openAddFromViewAll = () => {
    setListingAlertsModalOpen(false);
    setAddListingAlertModalOpen(true);
  };

  const tooltipDescription = () => {
    const hasActiveIdxAlerts = person.active_idx_alerts && person.active_idx_alerts > 0;
    const hasIdxAlerts = person.idx_alerts && person.idx_alerts > 0;
    if (hasActiveIdxAlerts) {
      return "Listing Alerts sending";
    }
    if (hasIdxAlerts) {
      return "Not sending Listing Alerts";
    }
    return "No Listing Alerts";
  };

  const getColorStyle = () => {
    const hasActiveIdxAlerts = person.active_idx_alerts && person.active_idx_alerts > 0;
    const hasIdxAlerts = person.idx_alerts && person.idx_alerts > 0;
    if (hasActiveIdxAlerts) {
      return "";
    }
    if (hasIdxAlerts) {
      return "!tw-text-brand-danger";
    }
    return "!tw-text-gray-50";
  };

  const closeAddListingAlertModal = () => {
    setAddListingAlertModalOpen(false);
    setIndexOfAlertToEdit(0);
  };

  return (
    <>
      <Tooltip
        trigger={
          <TextButton
            disabled={!emailDetails}
            className={getColorStyle()}
            size="medium"
            onClick={openListingAlertModal}
          >
            {person.idx_alerts || 0}
          </TextButton>
        }
        content={tooltipDescription()}
        placement="right"
      />
      {listingAlertsModalOpen && (
        <ViewAllListingAlertsModal
          onClose={closeListingAlertModal}
          listingAlerts={listingAlerts}
          openEditModal={openEditFromViewAll}
          handleDelete={deleteAlert}
          mlsIds={mlsIds}
          websiteUrl={websiteUrl}
          openAddModal={openAddFromViewAll}
          onDismiss={onDismiss}
          isDismiss={isDismiss}
          onToggleNotification={onToggleNotification}
          doesLeadHaveApp={doesLeadHaveApp}
        />
      )}
      {addListingAlertModalOpen && (
        <AddListingAlertModal
          person={person}
          closeListingAlertModal={closeAddListingAlertModal}
          mlsIds={mlsIds}
          geoCodeAccountAddress={geoCodeAccountAddress}
        />
      )}
      {editListingAlertModalOpen && (
        <EditListingAlertModal
          person={person}
          closeListingAlertModal={closeEditListingAlertModal}
          alert={listingAlerts[indexOfAlertToEdit]}
          mlsIds={mlsIds}
          geoCodeAccountAddress={geoCodeAccountAddress}
        />
      )}
    </>
  );
};

AllListingAlertPreview.propTypes = {
  person: PropTypes.shape().isRequired,
  emailDetails: PropTypes.shape(),
  geoCodeAccountAddress: PropTypes.string,
  doesLeadHaveApp: PropTypes.bool,
  onToggleCollectionNotification: PropTypes.func,
};

export default AllListingAlertPreview;
